/* eslint-disable max-len */

import React, {
    useState, useCallback, useRef, useImperativeHandle, forwardRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import PinchZoomPan from 'components/PinchZoomPan/PinchZoomPan';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import {
    radii, fontSizes, lineHeights, space, colors
} from 'style/config';
import LanguageLocale from 'utils/LanguageLocale';
import EN from 'components/PanZoom/locales/PanZoom_en_US.js';
import FR from 'components/PanZoom/locales/PanZoom_fr_CA.js';

const { getLocalisedDataFromResource } = LanguageLocale;
const getText = getLocalisedDataFromResource(EN, FR);

const ICON_SIZE = 24;
const ZOOM = 0.5;
const MIN_SCALE = 0.5;

const PanZoom = forwardRef((props, ref) => {
    const [scale, setScale] = useState(props.scale || 1);
    const [zoomSession, setZoomSession] = useState(0);
    const [showMsg, setShowMsg] = useState(props.showMsg);

    const pinchZoomPanRef = useRef(null);

    // Expose updateCoords for backward compatibility
    useImperativeHandle(
        ref,
        () => ({
            updateCoords: coords => {
                if (pinchZoomPanRef.current?.updatePosition) {
                    pinchZoomPanRef.current.updatePosition(coords);
                }
            }
        }),
        []
    );

    const fireZoomAnalytics = useCallback(() => {
        const actionInfo = 'product:alt-image:zoom';
        const linkName = 'product:alt-image:zoom';
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                actionInfo,
                linkName,
                eventStrings: [anaConsts.Event.EVENT_71]
            }
        });
    }, []);

    const zoom = useCallback(
        isZoomIn => {
            if (showMsg) {
                setShowMsg(false);
            }

            setScale(prevScale => {
                const nextScale = isZoomIn ? prevScale + ZOOM : prevScale - ZOOM;

                return Math.max(nextScale, MIN_SCALE);
            });
            // PinchZoomPan treats initialScale as an initial value. Bumping key starts a new
            // transform session from the updated scale when zoom buttons are used.
            setZoomSession(prevSession => prevSession + 1);
            fireZoomAnalytics();
        },
        [fireZoomAnalytics, showMsg]
    );

    const handleTransformChange = useCallback(
        (nextScale, position) => {
            if (showMsg && (nextScale !== 1 || position.x !== 0 || position.y !== 0)) {
                setShowMsg(false);
            }
        },
        [showMsg]
    );

    const handleZoomIn = useCallback(() => {
        zoom(true);
    }, [zoom]);

    const handleZoomOut = useCallback(() => {
        zoom(false);
    }, [zoom]);

    const isScaleControlled = typeof props.scale === 'number';
    const resolvedScale = isScaleControlled ? props.scale : scale;
    const { showPinchBottom } = props;
    const pinchZoomPanKey = isScaleControlled ? `controlled-${resolvedScale}` : `interactive-${zoomSession}`;

    return (
        <React.Fragment>
            <div
                css={styles.container}
                style={{
                    width: props.width,
                    height: props.height
                }}
            >
                <PinchZoomPan
                    ref={pinchZoomPanRef}
                    key={pinchZoomPanKey}
                    width='100%'
                    height='100%'
                    initialScale={resolvedScale}
                    minScale={0.5}
                    maxScale={3}
                    onTransformChange={handleTransformChange}
                    enablePan={!isScaleControlled}
                    enablePinch={!isScaleControlled}
                >
                    {props.children}
                </PinchZoomPan>
                {showMsg && (
                    <div
                        css={showPinchBottom ? styles.pinchBottom : styles.msg}
                        children={showPinchBottom ? getText('pinchToZoom') : getText('pinchAndZoom')}
                    />
                )}
            </div>
            {props.disableButtons || (
                <div css={styles.zoomControls}>
                    <button
                        type='button'
                        css={styles.zoomControlsBtn}
                        onClick={handleZoomIn}
                    >
                        <svg
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                            viewBox='0 0 24 24'
                        >
                            <path d='M12.8 3h-1.6v8.2H3v1.6h8.2V21h1.6v-8.2H21v-1.6h-8.2z' />
                        </svg>
                    </button>
                    <button
                        type='button'
                        css={styles.zoomControlsBtn}
                        onClick={handleZoomOut}
                    >
                        <svg
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                            viewBox='0 0 24 24'
                        >
                            <path d='M2 12.9h20v-1.8H2z' />
                        </svg>
                    </button>
                </div>
            )}
        </React.Fragment>
    );
});

const styles = {
    container: {
        position: 'relative',
        overflow: 'hidden'
    },
    msg: {
        pointerEvents: 'none',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: radii[2],
        fontSize: fontSizes.md,
        lineHeight: lineHeights.tight,
        padding: `${space[3]}px ${space[5]}px`
    },
    pinchBottom: {
        pointerEvents: 'none',
        position: 'absolute',
        bottom: '18%',
        right: space.container,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: radii[2],
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        fontWeight: 'var(--font-weight-bold)',
        padding: '.5em 1em',
        color: colors.white
    },
    zoomControls: {
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0
    },
    zoomControlsBtn: {
        lineHeight: 0,
        height: ICON_SIZE + space[4] * 2,
        padding: `0 ${space[2]}px`
    }
};

export default wrapFunctionalComponent(PanZoom, 'PanZoom');
